import { io } from "socket.io-client";
import Cookies from "js-cookie";
import {
    CONTACT_SYNC_SOCKET_EVENTS,
    shouldTriggerContactRefresh,
} from "../utils/contactListSync";

const SOCKET_URL = "https://phonebookplus.app";
const SOCKET_PATH = "/api/socket.io/";
const SOCKET_TIMEOUT = 20000;
const SOCKET_RECONNECT_DELAY = 1000;
const SOCKET_RECONNECT_ATTEMPTS = 5;

let socket = null;
let activeToken = null;
let globalContactSyncAttached = false;
/** @type {Set<(event: { type: string, eventName?: string, payload?: unknown, reason?: string }) => void>} */
const contactSyncListeners = new Set();
/** @type {Map<string, Map<string, { message: object, timeoutId?: number }>>} */
const pendingByContact = new Map();
const uploadListeners = new Set();

const notifyContactSync = (event) => {
    contactSyncListeners.forEach((listener) => {
        try {
            listener(event);
        } catch (_) {
            /* ignore */
        }
    });
};

export const subscribeContactSync = (listener) => {
    contactSyncListeners.add(listener);
    return () => contactSyncListeners.delete(listener);
};

export const requestContactsRefresh = (reason = "manual") => {
    notifyContactSync({ type: "refresh", reason, payload: null });
};

const attachGlobalContactSyncHandlers = (activeSocket) => {
    if (!activeSocket || globalContactSyncAttached) return;

    globalContactSyncAttached = true;

    const emitSocketEvent = (eventName, payload) => {
        notifyContactSync({ type: "socket", eventName, payload });
    };

    CONTACT_SYNC_SOCKET_EVENTS.forEach((eventName) => {
        activeSocket.on(eventName, (payload) => emitSocketEvent(eventName, payload));
    });

    activeSocket.onAny((eventName, payload) => {
        if (!shouldTriggerContactRefresh(eventName)) return;
        if (CONTACT_SYNC_SOCKET_EVENTS.includes(eventName)) return;
        emitSocketEvent(eventName, payload);
    });
};

const resetGlobalContactSyncHandlers = () => {
    globalContactSyncAttached = false;
};

const notifyUpload = (event, payload) => {
    uploadListeners.forEach((fn) => {
        try {
            fn(event, payload);
        } catch (err) {
            }
    });
};

export const subscribeUploadEvents = (listener) => {
    uploadListeners.add(listener);
    return () => uploadListeners.delete(listener);
};

export const getChatSocket = () => socket;

export const ensureChatSocket = (token = Cookies.get("auth_token")) => {
    if (!token) return null;

    if (socket && activeToken === token) {
        attachGlobalContactSyncHandlers(socket);
        return socket;
    }

    if (socket) {
        try {
            socket.removeAllListeners();
            socket.disconnect();
        } catch (_) {
            /* ignore */
        }
        socket = null;
        resetGlobalContactSyncHandlers();
    }

    activeToken = token;
    socket = io(SOCKET_URL, {
        path: SOCKET_PATH,
        transports: ["polling"],
        query: { token },
        upgrade: false,
        reconnection: true,
        reconnectionDelay: SOCKET_RECONNECT_DELAY,
        reconnectionAttempts: SOCKET_RECONNECT_ATTEMPTS,
        timeout: SOCKET_TIMEOUT,
        forceNew: true,
    });

    attachGlobalContactSyncHandlers(socket);

    return socket;
};

export const disconnectChatSocket = () => {
    pendingByContact.forEach((contactMap) => {
        contactMap.forEach((entry) => {
            if (entry.timeoutId) clearTimeout(entry.timeoutId);
        });
    });
    pendingByContact.clear();

    if (socket) {
        try {
            socket.removeAllListeners();
            socket.disconnect();
        } catch (_) {
            /* ignore */
        }
    }
    socket = null;
    activeToken = null;
    resetGlobalContactSyncHandlers();
};

const getContactMap = (contactId) => {
    const key = String(contactId);
    if (!pendingByContact.has(key)) {
        pendingByContact.set(key, new Map());
    }
    return pendingByContact.get(key);
};

export const trackPendingUpload = (contactId, tempId, message, timeoutId, { notify = true } = {}) => {
    const entry = { message: { ...message }, timeoutId };
    getContactMap(contactId).set(String(tempId), entry);
    if (notify) {
        notifyUpload("added", { contactId, tempId, message: entry.message });
    }
};

export const patchPendingUpload = (contactId, tempId, patch, { notify = true } = {}) => {
    const map = pendingByContact.get(String(contactId));
    if (!map) return null;
    const entry = map.get(String(tempId));
    if (!entry) return null;
    if (patch.uploading === false && entry.timeoutId) {
        clearTimeout(entry.timeoutId);
        entry.timeoutId = undefined;
    }
    entry.message = { ...entry.message, ...patch };
    if (notify) {
        notifyUpload("updated", { contactId, tempId, message: entry.message });
    }
    return entry.message;
};

export const clearPendingUpload = (contactId, tempId, { notify = true } = {}) => {
    const map = pendingByContact.get(String(contactId));
    if (!map) return;
    const entry = map.get(String(tempId));
    if (entry?.timeoutId) clearTimeout(entry.timeoutId);
    map.delete(String(tempId));
    if (map.size === 0) pendingByContact.delete(String(contactId));
    if (notify) {
        notifyUpload("removed", { contactId, tempId });
    }
};

export const getPendingUploadMessages = (contactId) => {
    const map = pendingByContact.get(String(contactId));
    if (!map) return [];
    return [...map.values()].map((entry) => entry.message);
};

export const hasActiveUploads = () => {
    for (const map of pendingByContact.values()) {
        for (const entry of map.values()) {
            if (entry.message?.uploading) return true;
        }
    }
    return false;
};

export const shouldFailUploadsOnDisconnect = (reason) => {
    // Keep uploads running through normal reconnects / navigation (socket stays alive).
    if (reason === "io client disconnect") return false;
    if (reason === "transport close" || reason === "ping timeout") return false;
    return true;
};

const normalizeUploadBaseName = (name) => {
    if (!name) return "";
    let base = String(name).split("/").pop() || String(name);
    base = base
        .replace(/\.[a-f0-9]{32,}\./gi, ".")
        .replace(/_[a-f0-9]{32,}\./gi, ".")
        .replace(/-[a-f0-9]{32,}\./gi, ".")
        .replace(/_[a-f0-9]+\./gi, ".")
        .replace(/-[a-f0-9]+\./gi, ".")
        .toLowerCase()
        .trim();
    return base.replace(/_[a-f0-9]{8,}$/gi, "").replace(/-[a-f0-9]{8,}$/gi, "");
};

const getUploadMessageTime = (message = {}) => {
    const raw =
        message?.created_at ||
        message?.timestamp ||
        message?.custom_time ||
        message?.time ||
        message?.original?.created_at ||
        message?.original?.timestamp ||
        null;
    const time = raw ? new Date(raw).getTime() : 0;
    return Number.isFinite(time) ? time : 0;
};

const pendingMatchesServerMessage = (pending, serverMessage) => {
    if (serverMessage?.isUser === false) return false;

    const pendingBase = normalizeUploadBaseName(pending?.fileName || pending?.text);
    const serverBase = normalizeUploadBaseName(
        serverMessage?.fileName || serverMessage?.text || serverMessage?.original?.file_name
    );
    if (!pendingBase || !serverBase || pendingBase !== serverBase) return false;

    const pendingStart = pending?.uploadStartedAt || 0;
    if (!pendingStart) return true;

    const serverTime = getUploadMessageTime(serverMessage);
    return !serverTime || serverTime >= pendingStart - 30000;
};

export const mergeMessagesWithPending = (messages, contactId) => {
    const pending = getPendingUploadMessages(contactId);
    if (!pending.length) return messages;

    const existingTempIds = new Set(
        messages.flatMap((m) => [m.tempId, m._clientUploadId, m.id].filter(Boolean).map(String))
    );

    const toAppend = pending.filter((p) => {
        if (!p.uploading) return false;
        const id = String(p.tempId || p.id || "");
        if (id && existingTempIds.has(id)) return false;

        const alreadyInHistory = messages.some((m) => pendingMatchesServerMessage(p, m));
        return !alreadyInHistory;
    });

    if (!toAppend.length) return messages;
    return [...messages, ...toAppend];
};
